// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

/**
 * COB-340: email-verification landing page. The verification email
 * deep-links here as `/verify-email#token=<raw>` — token in the URL
 * fragment, never a query string, so it stays out of access logs,
 * CDN logs, and `Referer` (same rule as the shared-vault invite link).
 *
 * The token is consumed automatically on mount: unlike account
 * abandonment there is nothing destructive to confirm, and a replayed
 * click of an already-used link comes back 200 `already_verified`, so
 * auto-consume is idempotent from the user's point of view. The page
 * needs no session — the emailed single-use token IS the credential —
 * but when the tab does hold one, a successful confirm refreshes the
 * session's `emailVerified` so the banner drops without a re-login.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import { api, ApiError } from '../api.js';
import { useSession } from '../session-context.js';
import { ErrorScreen } from '../components/error-screen.js';

// ── Pure helpers (exported for unit tests) ────────────────────────────

/** Pull the raw verification token out of a URL fragment
 *  (`#token=<raw>`). Accepts the hash with or without a leading `#`;
 *  returns `null` when absent or empty. Mirrors `parseInviteToken` in
 *  pages/shared-vault-accept.tsx. */
export function parseVerifyEmailToken(hash: string): string | null {
  const h = hash.startsWith('#') ? hash.slice(1) : hash;
  const token = new URLSearchParams(h).get('token');
  return token && token.length > 0 ? token : null;
}

export type VerifyEmailPhase =
  | 'missing'
  | 'verifying'
  | 'verified'
  | 'already_verified'
  | 'dead'
  | 'invalid'
  | 'rate_limited'
  | 'error';

/** Map a failed `POST /auth/verify-email-confirm` to a page phase. The
 *  backend deliberately never uses 404/403 (CloudFront rewrites those
 *  to the SPA shell at the edge): `400` = the token was never valid,
 *  `410` = used / expired / superseded by the email's true owner
 *  verifying elsewhere. */
export function confirmErrorPhase(status: number): VerifyEmailPhase {
  if (status === 400) return 'invalid';
  if (status === 410) return 'dead';
  if (status === 429) return 'rate_limited';
  return 'error';
}

export function VerifyEmail() {
  const { isAuthenticated, refreshEmailVerification } = useSession();
  const [phase, setPhase] = useState<VerifyEmailPhase>(() =>
    parseVerifyEmailToken(window.location.hash) ? 'verifying' : 'missing',
  );
  // Guard against effect re-runs consuming the token twice (harmless
  // server-side — the replay comes back `already_verified` — but it
  // would flash the wrong terminal state).
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current || phase !== 'verifying') return;
    firedRef.current = true;
    const token = parseVerifyEmailToken(window.location.hash);
    if (!token) {
      setPhase('missing');
      return;
    }
    void (async () => {
      try {
        const { status } = await api.verifyEmailConfirm({ token });
        setPhase(status);
        // Same-tab session: drop the verify banner without a re-login.
        // Best-effort — the flip already committed server-side.
        if (isAuthenticated) {
          try {
            await refreshEmailVerification();
          } catch {
            /* banner refresh is cosmetic here */
          }
        }
      } catch (err) {
        setPhase(
          err instanceof ApiError ? confirmErrorPhase(err.status) : 'error',
        );
      }
    })();
    // Deliberately keyed on `phase` alone: the retry button re-enters
    // 'verifying', and the session bits are read at fire time.
  }, [phase]);

  const goHome = () => window.location.assign('/');

  switch (phase) {
    case 'verifying':
      return (
        <div class="error-screen">
          <div class="error-screen-card">
            <h1 class="error-screen-title">Verifying your email…</h1>
          </div>
        </div>
      );
    case 'verified':
      return (
        <ErrorScreen
          title="Email verified"
          body={
            <p>
              Your email address is confirmed. You can close this tab, or head
              back to Cobault.
            </p>
          }
          primaryLabel="Back to Cobault"
          onPrimary={goHome}
        />
      );
    case 'already_verified':
      return (
        <ErrorScreen
          title="Email already verified"
          body={<p>This email address was already confirmed — nothing to do.</p>}
          primaryLabel="Back to Cobault"
          onPrimary={goHome}
        />
      );
    case 'dead':
      return (
        <ErrorScreen
          title="This link has expired or was already used"
          body={
            <p>
              Verification links are single-use and expire after 24 hours. Sign
              in and use <strong>Resend email</strong> on the verification
              banner to get a fresh one.
            </p>
          }
          primaryLabel="Back to Cobault"
          onPrimary={goHome}
        />
      );
    case 'invalid':
      return (
        <ErrorScreen
          title="This link isn't valid"
          body={
            <p>
              Check that you opened the complete link from the email. If it
              still fails, sign in and use <strong>Resend email</strong> on the
              verification banner to get a fresh one.
            </p>
          }
          primaryLabel="Back to Cobault"
          onPrimary={goHome}
        />
      );
    case 'rate_limited':
      return (
        <ErrorScreen
          title="Too many attempts"
          body={<p>Please wait a minute, then reload this page to try again.</p>}
          primaryLabel="Reload"
          onPrimary={() => window.location.reload()}
          secondaryLabel="Back to Cobault"
          onSecondary={goHome}
        />
      );
    case 'error':
      return (
        <ErrorScreen
          title="Something went wrong"
          body={<p>We couldn't verify your email just now.</p>}
          primaryLabel="Try again"
          onPrimary={() => {
            firedRef.current = false;
            setPhase('verifying');
          }}
          secondaryLabel="Back to Cobault"
          onSecondary={goHome}
        />
      );
    case 'missing':
    default:
      return (
        <ErrorScreen
          title="Missing verification link"
          body={
            <p>
              This page needs the token from your verification email — open
              the link in the email directly. If you no longer have it, sign
              in and use <strong>Resend email</strong> on the verification
              banner.
            </p>
          }
          primaryLabel="Back to Cobault"
          onPrimary={goHome}
        />
      );
  }
}
